import * as React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  ICrashReportSummary,
  searchByOriginalCrashKey,
  downloadExport,
  importExport,
} from '../../services/crashExportService';

interface IImportExportState {
  searchKey: string;
  searching: boolean;
  searched: boolean;
  results: ICrashReportSummary[];
  downloading: boolean;
  importFile: File | null;
  overwrite: boolean;
  importing: boolean;
  showConfirm: boolean;
}

class ImportExport extends React.Component<{}, IImportExportState> {
  private fileInputRef = React.createRef<HTMLInputElement>();

  constructor(props) {
    super(props);
    this.state = {
      searchKey: '',
      searching: false,
      searched: false,
      results: [],
      downloading: false,
      importFile: null,
      overwrite: false,
      importing: false,
      showConfirm: false,
    };
  }

  private handleSearch = async () => {
    const key = this.state.searchKey.trim();
    if (!key) {
      toast.warning('Please enter an original crash key.');
      return;
    }

    this.setState({ searching: true, searched: false, results: [] });

    try {
      const response = await searchByOriginalCrashKey(key);
      if (response.ok && response.data) {
        this.setState({ results: response.data, searched: true });
      } else {
        this.setState({ results: [], searched: true });
        toast.error('The search failed. Please try again.');
      }
    } catch (error) {
      this.setState({ results: [], searched: true });
      toast.error('An error occurred while searching.');
    } finally {
      this.setState({ searching: false });
    }
  };

  private handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  };

  private handleDownload = async () => {
    const key = this.state.searchKey.trim();
    if (!key) {
      return;
    }

    this.setState({ downloading: true });

    try {
      await downloadExport(key);
    } catch (error) {
      toast.error('Failed to download the export file.');
    } finally {
      this.setState({ downloading: false });
    }
  };

  private handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file =
      event.target.files && event.target.files.length > 0
        ? event.target.files[0]
        : null;
    this.setState({ importFile: file });
  };

  private handleImportClick = () => {
    if (!this.state.importFile) {
      toast.warning('Please choose an export file to import.');
      return;
    }
    this.setState({ showConfirm: true });
  };

  private cancelImport = () => {
    this.setState({ showConfirm: false });
  };

  private confirmImport = async () => {
    const { importFile, overwrite } = this.state;
    if (!importFile) {
      this.setState({ showConfirm: false });
      return;
    }

    this.setState({ showConfirm: false, importing: true });

    try {
      const response = await importExport(importFile, overwrite);
      if (response.ok) {
        const result = response.data;
        const replaced = result && result.entitiesReplaced ? result.entitiesReplaced : 0;
        toast.success(
          replaced > 0
            ? `Import complete. ${replaced} existing record(s) were overwritten.`
            : 'Import complete.'
        );
        this.setState({ importFile: null });
        if (this.fileInputRef.current) {
          this.fileInputRef.current.value = '';
        }
      } else {
        const message =
          typeof response.data === 'string'
            ? (response.data as string)
            : 'The import failed. Please verify the file and try again.';
        toast.error(message);
      }
    } catch (error) {
      toast.error('An error occurred during import.');
    } finally {
      this.setState({ importing: false });
    }
  };

  private formatDate = (value: string) => {
    if (!value) {
      return '';
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '';
  };

  render() {
    const {
      searchKey,
      searching,
      searched,
      results,
      downloading,
      importFile,
      overwrite,
      importing,
      showConfirm,
    } = this.state;

    return (
      <div className='p-3'>
        <div className='panel'>
          <div className='panel-hdr'>
            <h2>Export</h2>
          </div>
          <div className='panel-container show'>
            <div className='panel-content'>
              <div className='form-group'>
                <label htmlFor='originalCrashKey'>Original Crash Key</label>
                <div className='d-flex'>
                  <input
                    id='originalCrashKey'
                    type='text'
                    className='form-control mr-2'
                    style={{ maxWidth: '320px' }}
                    value={searchKey}
                    placeholder='Enter original crash key'
                    onChange={(e) => this.setState({ searchKey: e.target.value })}
                    onKeyDown={this.handleSearchKeyDown}
                  />
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={this.handleSearch}
                    disabled={searching}
                  >
                    {searching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {searched && results.length === 0 && (
                <div className='alert alert-warning mt-3'>
                  No reports were found for that original crash key.
                </div>
              )}

              {results.length > 0 && (
                <div className='mt-3'>
                  <table className='table table-bordered table-striped'>
                    <thead>
                      <tr>
                        <th>Report Id</th>
                        <th>Report Type</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Queue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((report) => (
                        <tr key={report.reportId}>
                          <td>{report.reportId}</td>
                          <td>{report.reportType}</td>
                          <td>{this.formatDate(report.date)}</td>
                          <td>{report.status}</td>
                          <td>{report.queue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <button
                    type='button'
                    className='btn btn-success'
                    onClick={this.handleDownload}
                    disabled={downloading}
                  >
                    {downloading ? 'Downloading...' : 'Download'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='panel mt-4'>
          <div className='panel-hdr'>
            <h2>Import</h2>
          </div>
          <div className='panel-container show'>
            <div className='panel-content'>
              <div className='form-group'>
                <label htmlFor='importFile'>Export File</label>
                <input
                  id='importFile'
                  ref={this.fileInputRef}
                  type='file'
                  className='form-control-file'
                  onChange={this.handleFileChange}
                />
              </div>

              <div className='form-group'>
                <div className='custom-control custom-checkbox'>
                  <input
                    id='overwriteExisting'
                    type='checkbox'
                    className='custom-control-input'
                    checked={overwrite}
                    onChange={(e) =>
                      this.setState({ overwrite: e.target.checked })
                    }
                  />
                  <label
                    className='custom-control-label'
                    htmlFor='overwriteExisting'
                  >
                    Overwrite Existing
                  </label>
                </div>
              </div>

              <button
                type='button'
                className='btn btn-primary'
                onClick={this.handleImportClick}
                disabled={importing || !importFile}
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>

        <Modal show={showConfirm} onHide={this.cancelImport}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Import</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {overwrite ? (
              <p>
                If a report with the same original crash key already exists, all of
                its data will be <strong>overwritten and permanently replaced</strong>.
                This action is <strong>not recoverable</strong>. Are you sure you
                want to continue?
              </p>
            ) : (
              <p>
                The reports in this file will be imported as new records. Are you
                sure you want to continue?
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={this.cancelImport}>
              Cancel
            </Button>
            <Button variant='danger' onClick={this.confirmImport}>
              Import
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default ImportExport;
